// sort-imports-ignore
/* eslint-disable import/first */
// Import polyfills and setup error reporting system
import "./_bootstrap";
import {
    Provider as ReactivatedProvider,
    getServerData,
    getTemplate,
} from "@reactivated";
import React from "react";
import { Provider as ReduxProvider } from "react-redux";
import { store } from "./store";
import { initPage } from "@thelabnyc/thelabui/src/index";
import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

// Initialize the rest of the application
import Modal from "react-modal";
import {
    sendGTMUserHash,
    kuidListeners,
} from "tsi-common-react/src/utils/analytics";
import { initWidgets } from "tsi-common-react/src/forms/initWidgets";

// Remove no-js class from HTML tag. On a full-document React page React owns
// <html className="no-js"> (rendered for the no-JS fallback), so an eager
// pre-hydration removal here would race hydration and mismatch -- those pages
// drop it post-hydration in PageSkeleton's effect instead. Island pages have a
// Django-rendered <html> with no hydration to race, so remove it eagerly.
const document = getDocument();
const _winNoJs = getWindow() as (Window & Record<string, unknown>) | null;
if (!_winNoJs?.["__PRELOADED_PROPS__"]) {
    const htmlElem = document?.querySelector("html");
    if (htmlElem) {
        htmlElem.classList.remove("no-js");
    }
}

// Load CSS
import "./styles/main.scss";

// Set tsi-common-react configuration
import "./localization";

const Provider = (props: React.ComponentProps<typeof ReactivatedProvider>) => {
    const { children, ...otherProps } = props;
    return (
        <ReactivatedProvider {...otherProps}>
            <ReduxProvider store={store}>{children}</ReduxProvider>
        </ReactivatedProvider>
    );
};

// In Vite dev mode, this module can execute twice under different URLs
// (bare vs ?t= cache-busted). Every module-level initialization must run
// exactly once: a second pass double-hydrates, double-registers analytics,
// and double-binds the initWidgets event handlers (paired class/aria
// toggles then cancel each other out -- e.g. accordions stop opening).
// Side-effect imports below are safe: child module URLs are canonical, so
// Vite caches them across both entry executions.
const _w = getWindow() as (Window & Record<string, unknown>) | null;
if (!_w?.["__tsi_initPage"]) {
    if (_w) _w["__tsi_initPage"] = true;
    initPage({
        Provider,
        getServerData,
        getTemplate,
    }).catch((e) => {
        console.error(e);
    });

    // Update the user hash in GTM on page load
    // NOTE: Changed `window` to `_window` via getWindow() to avoid accessing global `window` at module scope
    // which prevented MainMenu from mounting. (Ticket no: #30493)
    const _window = getWindow();
    if (_window) {
        _window.onload = () => {
            sendGTMUserHash();
        };
    }
    kuidListeners();

    // Set the root app element for react-modal
    if (document) {
        if (document.querySelector("body > .layout")) {
            Modal.setAppElement("body > .layout");
        } else if (document.querySelector("#root")) {
            Modal.setAppElement("#root");
        }
    }

    // Load Site Components
    initWidgets();
}

import "./init";
import "./apps/checkout/index";
import "./apps/product-detail/index";
import "./apps/reviews/index";
